import styled from 'styled-components';
import { s } from './Pattern.styles';
import { usePatternContext } from '../../../../context/PatternContext';
import {
  InitialInvestOptions,
  InitialProfitOptions,
  investmentTypeMap,
  periodMap,
  prcReliabilityMap,
  profitRateMap,
  profitStatusMap,
  purposeMap,
  revenueTypeMap,
  valueStatusMap,
} from '../data';

interface ChipItem {
  key: string;
  label?: string;
  value: string;
  onRemove: () => void;
}

interface Props {
  tab: string;
}

function SelectedOptions({ tab }: Props) {
  const { investOptions, setInvestOptions, profitOptions, setProfitOptions } =
    usePatternContext();
  const handleReset = () => {
    if (tab === '투자') {
      setInvestOptions(InitialInvestOptions);
    }
    if (tab === '수익') {
      setProfitOptions(InitialProfitOptions);
    }
  };

  const currentYear = new Date().getFullYear();

  const getRangeLabel = ([start, end]: [number, number]) => {
    if (start === 1980 && end === 2025) {
      return '전체';
    }

    if (start !== 1980 && end === 2025) {
      const age = currentYear - start;
      return `${age}년 이하`;
    }

    if (start === 1980 && end !== 2025) {
      const age = currentYear - end;
      return `${age}년 이상`;
    }

    if (start !== 1980 && end !== 2025) {
      const ageStart = currentYear - end;
      const ageEnd = currentYear - start;
      return `${ageStart}년 ~ ${ageEnd}년`;
    }

    return '';
  };

  const calculatePrice = (price: string) => {
    if (!price) return '';
    const value = Number(price);

    if (value >= 10000) {
      const eok = (value / 10000).toFixed(0);
      return `${parseFloat(eok)}억원`;
    } else {
      return `${value}만원`;
    }
  };

  const investChips: ChipItem[] = (() => {
    const list: ChipItem[] = [];

    // 지역 (시/도, 시군구, 읍면동 — 값이 있는 것만 칩으로)
    const { sido, sigungu, emd } = investOptions.region || {};
    if (sido) {
      list.push({
        key: 'region',
        value: [sido, sigungu, emd].filter((r) => r !== '').join(' > '),
        onRemove: () =>
          setInvestOptions((prev: any) => ({
            ...prev,
            region: { sido: '', sigungu: '', emd: '' },
            pnu: '',
          })),
      });
    }

    // 물건 종류
    if (investOptions.category) {
      list.push({
        key: 'category',
        value: investOptions.category,
        onRemove: () =>
          setInvestOptions((prev: any) => ({
            ...prev,
            category: '',
          })),
      });
    }

    // 물건종류 상세
    if (investOptions.categoryDetail) {
      list.push({
        key: 'categoryDetail',
        value: investOptions.categoryDetail,
        onRemove: () =>
          setInvestOptions((prev: any) => ({
            ...prev,
            categoryDetail: '',
          })),
      });
    }

    // 투자 기간
    if (investOptions.investmentPeriod !== 'A') {
      list.push({
        key: 'investmentPeriod',
        label: '투자기간',
        value: periodMap[investOptions.investmentPeriod],
        onRemove: () =>
          setInvestOptions((prev: any) => ({
            ...prev,
            investmentPeriod: 'A',
          })),
      });
    }

    // 투자 목적
    if (investOptions.investmentPurpose !== 'A') {
      list.push({
        key: 'investmentPurpose',
        value: purposeMap[investOptions.investmentPurpose],
        onRemove: () =>
          setInvestOptions((prev: any) => ({
            ...prev,
            investmentPurpose: 'A',
          })),
      });
    }

    // 노후년도
    if (investOptions.aging_start_year && investOptions.aging_end_year) {
      list.push({
        key: 'aging',
        label: '노후년도',
        value: getRangeLabel([
          Number(investOptions.aging_start_year),
          Number(investOptions.aging_end_year),
        ]),
        onRemove: () =>
          setInvestOptions((prev: any) => ({
            ...prev,
            aging_start_year: '',
            aging_end_year: '',
          })),
      });
    }
    // 용도지역
    if (investOptions.useArea.length > 0) {
      investOptions.useArea.forEach((ua: string, idx: number) => {
        list.push({
          key: `useArea.${idx}`,
          value: ua,
          onRemove: () =>
            setInvestOptions((prev: any) => ({
              ...prev,
              useArea: prev.useArea.filter((x: string) => x !== ua),
            })),
        });
      });
    }
    return list;
  })();

  const profitChips: ChipItem[] = (() => {
    const list: ChipItem[] = [];

    // 수익유형
    if (profitOptions.revenueType) {
      list.push({
        key: 'revenueType',
        value: revenueTypeMap[profitOptions.revenueType],
        onRemove: () =>
          setProfitOptions((prev: any) => ({
            ...prev,
            revenueType: 'A',
          })),
      });
    }

    // 부대비용 포함
    if (profitOptions.costIncluded !== 'N') {
      list.push({
        key: 'costIncluded',
        value: '부대비용 포함',
        onRemove: () =>
          setProfitOptions((prev: any) => ({
            ...prev,
            costIncluded: 'N',
          })),
      });
    }

    // 전체 투자금
    if (profitOptions.minInvestmentValue || profitOptions.maxInvestmentValue) {
      list.push({
        key: 'investmentValue',
        label: '전체 투자금',
        value: `${calculatePrice(profitOptions.minInvestmentValue)}~${calculatePrice(profitOptions.maxInvestmentValue)}`,
        onRemove: () =>
          setProfitOptions((prev: any) => ({
            ...prev,
            minInvestmentValue: '',
            maxInvestmentValue: '',
          })),
      });
    }

    // 투자금액 비율
    if (profitOptions.investmentRate) {
      list.push({
        key: 'investmentRate',
        label: investmentTypeMap[profitOptions.investmentType],
        value: `${profitOptions.investmentRate}%`,
        onRemove: () =>
          setProfitOptions((prev: any) => ({
            ...prev,
            investmentType: 'A',
            investmentRate: '',
          })),
      });
    }

    // 할인비율
    if (profitOptions.profitRate !== 'A') {
      list.push({
        key: 'profitRate',
        label: '할인 비율',
        value: profitRateMap[profitOptions.profitRate],
        onRemove: () =>
          setProfitOptions((prev: any) => ({
            ...prev,
            profitRate: 'A',
          })),
      });
    }

    // 유찰횟수
    if (
      profitOptions.customBidCnt &&
      (profitOptions.minBidCnt || profitOptions.maxBidCnt)
    ) {
      list.push({
        key: 'bidCnt',
        label: '유찰횟수',
        value: `${profitOptions.minBidCnt}~${profitOptions.maxBidCnt}`,
        onRemove: () =>
          setProfitOptions((prev: any) => ({
            ...prev,
            customBidCnt: false,
            minBidCnt: '',
            maxBidCnt: '',
          })),
      });
    }

    // 리스크요인
    list.push({
      key: 'risk',
      value: profitOptions.isRisk ? '위험감수' : '위험회피',
      onRemove: () =>
        setProfitOptions((prev: any) => ({
          ...prev,
          isRisk: false,
          risk: [],
        })),
    });

    // 리스크 상세요인
    if (profitOptions.risk.length > 0) {
      profitOptions.risk.forEach((ua: string, idx: number) => {
        list.push({
          key: `risk.${idx}`,
          value: ua,
          onRemove: () =>
            setProfitOptions((prev: any) => ({
              ...prev,
              risk: prev.risk.filter((x: string) => x !== ua),
            })),
        });
      });
    }

    // 매각 수익
    if (profitOptions.profitStatus) {
      list.push({
        key: 'profitStatus',
        value: profitStatusMap[profitOptions.profitStatus],
        onRemove: () =>
          setProfitOptions((prev: any) => ({
            ...prev,
            profitStatus: 'B',
          })),
      });
    }

    // 시장가치
    if (profitOptions.valueStatus) {
      list.push({
        key: 'valueStatus',
        value: valueStatusMap[profitOptions.valueStatus],
        onRemove: () =>
          setProfitOptions((prev: any) => ({
            ...prev,
            valueStatus: 'B',
          })),
      });
    }

    // 가격 신뢰도
    if (profitOptions.prcReliability) {
      list.push({
        key: 'prcReliability',
        label: '가격신뢰도',
        value: prcReliabilityMap[profitOptions.prcReliability],
        onRemove: () =>
          setProfitOptions((prev: any) => ({
            ...prev,
            prcReliability: 'B',
          })),
      });
    }

    // 사례 분석
    if (profitOptions.auctionDateYears) {
      list.push({
        key: 'auctionDateYears',
        label: '사례분석',
        value:
          profitOptions.auctionDateYears === '0'
            ? '전체 사례'
            : `최근 ${profitOptions.auctionDateYears}년 이내`,
        onRemove: () =>
          setProfitOptions((prev: any) => ({
            ...prev,
            auctionDateYears: '5',
          })),
      });
    }

    return list;
  })();

  const list = tab === '투자' ? investChips : profitChips;

  if (list.length === 0) return null;
  return (
    <Container>
      <Header>
        <Title>선택항목</Title>
        <ResetButton onClick={handleReset}>
          <img src="/assets/images/icon/reload-gray.svg" alt="" />
          <span>초기화</span>
        </ResetButton>
      </Header>
      <Content>
        {list.map((c) => (
          <Chip key={c.key}>
            <ChipLabel>{c.label}</ChipLabel>
            {c.value}
            <DeleteButton
              src="/assets/images/close/close-alternative.svg"
              alt="삭제"
              onClick={c.onRemove}
            />
          </Chip>
        ))}
      </Content>
    </Container>
  );
}

export default SelectedOptions;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  padding-top: 16px;
  border-top: 1px solid #70737c29;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const Title = styled.div`
  color: #222;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const ResetButton = styled.button`
  outline: none;
  background-color: transparent;
  border: none;
  cursor: pointer;

  display: flex;
  padding: 4px;
  justify-content: center;
  align-items: center;
  gap: 2px;

  color: #616161;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  gap: 8px;
  align-self: stretch;
  flex-wrap: wrap;
`;

const Chip = styled.div`
  display: flex;
  padding: 4px 8px;
  align-items: center;
  gap: 2px;
  border-radius: 9999px;
  background: #f7f8fc;

  color: #222;
  font-size: 13px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;
`;

const ChipLabel = styled.div`
  color: rgba(55, 56, 60, 0.61);
`;

const DeleteButton = styled.img`
  cursor: pointer;

  width: 16px;
  height: 16px;
`;
